import ExtraInfoItem from "common/ExtraDetails/ExtraInfoItem";
import React, { CSSProperties, ReactNode } from "react";
import {
  getBotAmountDisplay,
  getBotLongShortDisplay,
} from "@shared/features/bots/utils";
import {
  useGetSecondsTimeoutDisplay,
  useSharedBotSummary,
} from "@shared/features/bots/hooks";
import {
  useExchangesMetadata,
  useSubUserPermissions,
  useUser,
} from "@shared/utils/hooks/misc";
import TextWithIcon from "common/Text/TextWithIcon";
import { typography } from "@shared/common/Themes/typography";
import {
  ENTRY_UUID,
  ICON_TYPES,
  SIGNAL_ENTRY_UUID,
  SIGNAL_TAKE_PROFIT_UUID,
  TAKE_PROFIT_UUID,
  TIME_STOP_FEATURE_FLAG,
} from "@shared/common/constants";
import {
  BacktestingRunType,
  BaseBotData,
  BotData,
  BotType,
  LanguageNameSpaces,
  OrderTypes,
  OverrideFieldType,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  ADVANCED_KEY_TO_INPUT_DISPLAY_NAME,
  KEYWORD_TO_SYMBOL_TITLE,
} from "@shared/features/bots/BotForm/common/constants";
import BotStrategySummary from "features/bots/BotDrawer/BotStrategySummary";
import MultiSymbolMarginSelect from "features/bots/BotForm/MultiSymbolMargin/MultiSymbolMarginSelect.tsx";
import Flex from "common/Boxes/Flex";
import Link from "common/Typography/Link";
import CoinsIconsGroup from "features/portfolio/CoinsIconsGroup";
import {
  FIRST_ENTRY_AS_MARKET_FORM_ITEM_TITLE,
  TV_ALERT_SYMBOL,
  USING_CHANNEL_SETTING,
} from "@shared/features/bots/constants";
import BotOverrideDisplay from "features/bots/BotDrawer/BotOverrideDisplay";
import Text from "common/Typography/Text";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { getAdvancedKeys } from "@shared/features/bots/BotForm/common/utils";
import { toFixed } from "@shared/utils/numbers";
import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title";
import TradingViewUUID from "features/bots/common/TradingViewUUID";
import TrailingStopDisplay from "features/bots/BotDrawer/TrailingStopDisplay";
import { useEmptyState } from "common/Icons/useEmptyState.tsx";
import { HelperTooltip } from "common/Tooltips/HelperTooltip.tsx";
import ViewOnlyOperationHours from "features/bots/BotForm/OperationHours/ViewOnlyOperationHours.tsx";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup.tsx";
import { TvSymbol, TvSymbolDarkMode } from "common/Icons/Icons.tsx";
import { capitalizeFirstLetter } from "@shared/common/utils.ts";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
  getPanelShadow,
} from "common/NewLook/tokens.ts";

interface BotSummaryProps {
  botData: BaseBotData | BotData;
  style?: CSSProperties;
  showAmount?: boolean;
  showAccount?: boolean;
  showName?: boolean;
  isChannelConfiguration?: boolean;
  backtesting?: BacktestingRunType;
}

const BotSummary = ({
  botData,
  style,
  showAmount: propsShowAmount,
  showAccount,
  showName,
  isChannelConfiguration,
  backtesting,
}: BotSummaryProps) => {
  const { user } = useUser();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { ConfigurationSearch } = useEmptyState();
  const { exchangeToDisplayName } = useExchangesMetadata();
  const { isSubUser } = useSubUserPermissions();
  const theme = useTheme();
  const {
    symbolsData,
    accountData,
    subGroupData,
    showGeneralSection,
    marginDisplay,
    longShortDisplay,
    trailingEntryDisplay,
    trailingTakeProfitDisplay,
    trailingTakeProfitLastTargetOnly,
    defaultStopDisplay,
    stopTimeoutData,
    trailingStopDisplay,
    cancelOnProfitBeforeEntryDisplayData,
    showAdvancedSection,
    isAdvanced,
    pricePrecision,
    showTakeProfitStrategy,
    showTrailingStop,
    firstEntryGraceDisplay,
    stopTypeDisplay,
    stopLimitPriceReductionDisplay,
    showNumContinuousStops,
    defaultTakeProfitData,
    dynamicTakeProfitData,
    isSignalsBot,
    sectionsDisplayData,
    skipWithoutStopLossDisplay,
    initialFundsData,
    isTradingViewBot,
    showAmount,
    alternativeUsdPairsData,
    takeProfitGraceDisplay,
    operationHoursData,
    exchangesData,
  } = useSharedBotSummary(
    botData,
    showAccount,
    isChannelConfiguration,
    backtesting,
    undefined,
    propsShowAmount
  );
  const { getSecondsTimeoutDisplay } = useGetSecondsTimeoutDisplay();
  const isTradingViewSignalsBot =
    botData.bot_type === BotType.TRADING_VIEW_SIGNALS;
  const isHedgingBot = botData.bot_type === BotType.HEDGING;

  const valueFlex = "0 0 50%";

  /**
   * One settings section. New look: a panel with its title inside (the
   * rows under a hairline), like the platform's other titled sections;
   * classic: a heading over the rows.
   */
  const renderSection = (
    title: ReactNode,
    content: ReactNode,
    titleStyle?: CSSProperties
  ) =>
    theme.isNewLook ? (
      <NewLookSection>
        <NewLookSectionTitle>{title}</NewLookSectionTitle>
        <NewLookSectionBody>{content}</NewLookSectionBody>
      </NewLookSection>
    ) : (
      <>
        <SectionTitle style={titleStyle}>{title}</SectionTitle>
        {content}
      </>
    );

  const getMarginSummary = () => {
    if (isHedgingBot) return;
    if (botData.bot_type === BotType.SIGNAL) {
      if (getBotLongShortDisplay(botData, "")) {
        return (
          <ExtraInfoItem
            name={t("labels.margin")}
            autoHeight={true}
            value={
              botData.symbol_to_leverage_data ? (
                <MultiSymbolMarginSelect
                  isViewOnly={true}
                  getTrigger={(previewText) => {
                    return (
                      <>
                        <Flex style={{ width: "100%" }}>
                          {previewText}
                          <Link>{t("buttons.see_all")}</Link>
                        </Flex>
                        {!isChannelConfiguration && (
                          <BotOverrideDisplay
                            botData={botData}
                            overrideType={OverrideFieldType.LEVERAGE}
                          />
                        )}
                      </>
                    );
                  }}
                  botData={botData}
                />
              ) : (
                t(USING_CHANNEL_SETTING)
              )
            }
            valueFlex={valueFlex}
          />
        );
      }
    } else if (marginDisplay) {
      return (
        <ExtraInfoItem
          name={t("labels.margin")}
          value={marginDisplay}
          valueFlex={valueFlex}
        />
      );
    }
  };

  const getHedgingDisplayValue = (longExposureRatio?: unknown) => {
    if (typeof longExposureRatio !== "number") return;
    const longHedgingRatio = toFixed(longExposureRatio * 100, 2);
    const shortHedgingRatio = toFixed(100 - longHedgingRatio, 2);
    return `${t("labels.long")} ${longHedgingRatio}% / ${t(
      "labels.short"
    )} ${shortHedgingRatio}%`;
  };

  const getHedgingSummary = () => {
    if (!isHedgingBot) return;
    const lowerHedgingValue = getHedgingDisplayValue(
      botData.lower_price_long_exposure_ratio
    );
    const upperHedgingValue = getHedgingDisplayValue(
      botData.upper_price_long_exposure_ratio
    );
    const fixedHedgingValue = getHedgingDisplayValue(
      botData.long_exposure_ratio
    );
    return (
      <>
        {lowerHedgingValue && (
          <ExtraInfoItem
            name={t("labels.lower_boundary")}
            value={lowerHedgingValue}
            valueFlex={valueFlex}
          />
        )}
        {upperHedgingValue && (
          <ExtraInfoItem
            name={t("labels.upper_boundary")}
            value={upperHedgingValue}
            valueFlex={valueFlex}
          />
        )}
        {!lowerHedgingValue && !upperHedgingValue && fixedHedgingValue && (
          <ExtraInfoItem
            name={t("labels.fixed_hedging")}
            value={fixedHedgingValue}
            valueFlex={valueFlex}
          />
        )}
      </>
    );
  };

  const getSymbolsPreview = () => {
    if (!symbolsData) return;
    if (symbolsData?.isMultiSymbol) {
      return (
        <MultiSymbolMarginSelect
          isViewOnly={true}
          getTrigger={() => {
            return (
              <Flex>
                <CoinsIconsGroup
                  symbols={symbolsData.symbols}
                  exchange={botData.exchange}
                  size={24}
                  style={{ marginLeft: 8, marginTop: 3 }}
                  maxCount={8}
                  hideTooltips={true}
                />
                {symbolsData.numSymbols > 8 && (
                  <StyledLink $isMobile={isMobile}>
                    {t("buttons.see_all")}
                  </StyledLink>
                )}
              </Flex>
            );
          }}
          botData={botData}
          isSymbolsOnly={true}
          alwaysDisplay={!!backtesting}
        />
      );
    } else if (symbolsData?.isFromTvAlert) {
      const Icon = theme.isDarkMode ? TvSymbolDarkMode : TvSymbol;
      return (
        <Flex $justifyContent="flex-start">
          <Icon style={{ marginRight: 8, borderRadius: 4 }} />
          {TV_ALERT_SYMBOL}
        </Flex>
      );
    }
    return (
      <TextWithIcon
        coinOrExchangeName={symbolsData.symbol}
        text={symbolsData.symbol}
        iconType={ICON_TYPES.COIN}
      />
    );
  };

  const getSymbols = () => {
    if (!symbolsData || !symbolsData.shouldDisplay) return;
    return (
      <ExtraInfoItem
        name={symbolsData.title}
        value={
          <>
            {getSymbolsPreview()}
            {symbolsData.includeNewlyListedDisplay && (
              <Text $size="S" type="secondary" style={{ marginTop: 6 }}>
                {symbolsData.includeNewlyListedDisplay}
              </Text>
            )}
          </>
        }
        valueFlex={valueFlex}
        autoHeight={true}
      />
    );
  };

  const defaultTakeProfit = !!defaultTakeProfitData?.shouldDisplay && (
    <ExtraInfoItem
      key={defaultTakeProfitData.title}
      name={defaultTakeProfitData.title}
      value={
        typeof defaultTakeProfitData.value === "string" ? (
          defaultTakeProfitData.value
        ) : (
          <>
            <div>{defaultTakeProfitData.value?.distance}</div>
            {defaultTakeProfitData.showBaseline && (
              <div>{defaultTakeProfitData.value?.baseline}</div>
            )}
          </>
        )
      }
      autoHeight={true}
      valueFlex={valueFlex}
    />
  );

  const getTakeProfitsSection = () => {
    if (!sectionsDisplayData?.takeProfits) return;
    return renderSection(
      t("tabs.take_profits"),
      <>
        {!isSignalsBot && defaultTakeProfit}
        {showTakeProfitStrategy && (
          <BotStrategySummary
            botData={botData}
            orderType={OrderTypes.TAKE_PROFIT}
            pricePrecision={pricePrecision}
            isAdvanced={isAdvanced}
            isChannelConfiguration={isChannelConfiguration}
            defaultTakeProfitDisplay={
              isSignalsBot ? defaultTakeProfit : undefined
            }
          />
        )}
        {trailingTakeProfitDisplay && (
          <ExtraInfoItem
            name={t("labels.trailing_take_profit")}
            autoHeight={true}
            value={
              <>
                {trailingTakeProfitDisplay}
                {trailingTakeProfitLastTargetOnly && (
                  <div>
                    <Text $size="S" type="secondary">
                      * {t("labels.trailing_take_profit_last_target_only")}
                    </Text>
                  </div>
                )}
                {!isChannelConfiguration && (
                  <BotOverrideDisplay
                    botData={botData}
                    overrideType={OverrideFieldType.TRAILING_TAKE_PROFIT}
                    isChannelValue={
                      trailingTakeProfitDisplay === t(USING_CHANNEL_SETTING)
                    }
                  />
                )}
              </>
            }
            valueFlex={valueFlex}
          />
        )}
        {dynamicTakeProfitData?.shouldDisplay && (
          <ExtraInfoItem
            name={dynamicTakeProfitData.title}
            value={dynamicTakeProfitData.value}
            valueFlex={valueFlex}
          />
        )}
        {!!takeProfitGraceDisplay && (
          <ExtraInfoItem
            name={t("labels.take_profit_grace")}
            autoHeight={true}
            value={
              <>
                <SectionValue>{takeProfitGraceDisplay.percent}</SectionValue>
                {takeProfitGraceDisplay.targetExchange && (
                  <SectionValue>{`${t("labels.target_exchange")}: ${
                    exchangeToDisplayName[
                      takeProfitGraceDisplay.targetExchange
                    ] ?? takeProfitGraceDisplay.targetExchange
                  }`}</SectionValue>
                )}
              </>
            }
            valueFlex={valueFlex}
          />
        )}
      </>
    );
  };

  const getEntriesSection = () => {
    if (!sectionsDisplayData?.entries && !isHedgingBot) return;
    return renderSection(
      isHedgingBot
        ? t("labels.hedging")
        : botData.bot_type === BotType.GRID
        ? t("labels.grids")
        : t("labels.entries"),
      <>
        <BotStrategySummary
          botData={botData}
          orderType={OrderTypes.ENTRY}
          pricePrecision={pricePrecision}
          isAdvanced={isAdvanced}
          isChannelConfiguration={isChannelConfiguration}
        />
        {getHedgingSummary()}
        {trailingEntryDisplay && (
          <ExtraInfoItem
            name={t("labels.trailing_entry")}
            autoHeight={true}
            value={
              <>
                {trailingEntryDisplay}
                {!isChannelConfiguration && (
                  <BotOverrideDisplay
                    botData={botData}
                    overrideType={OverrideFieldType.TRAILING_ENTRY}
                    isChannelValue={
                      trailingEntryDisplay === t(USING_CHANNEL_SETTING)
                    }
                  />
                )}
              </>
            }
            valueFlex={valueFlex}
          />
        )}
        {!!firstEntryGraceDisplay && (
          <ExtraInfoItem
            name={t(FIRST_ENTRY_AS_MARKET_FORM_ITEM_TITLE)}
            autoHeight={true}
            value={
              <>
                <SectionValue>{firstEntryGraceDisplay.percent}</SectionValue>
                {firstEntryGraceDisplay.baseline && (
                  <SectionValue>{firstEntryGraceDisplay.baseline}</SectionValue>
                )}
                {firstEntryGraceDisplay.targetExchange && (
                  <SectionValue>{`${t("labels.target_exchange")}: ${
                    exchangeToDisplayName[
                      firstEntryGraceDisplay.targetExchange
                    ] ?? firstEntryGraceDisplay.targetExchange
                  }`}</SectionValue>
                )}
              </>
            }
            valueFlex={valueFlex}
          />
        )}
      </>,
      { marginTop: showGeneralSection ? undefined : 0 }
    );
  };

  const getStopSection = () => {
    if (!sectionsDisplayData?.stop) return;
    return renderSection(
      t("tabs.stop"),
      <>
        {!!defaultStopDisplay && (
          <ExtraInfoItem
            name={t("labels.default_stop")}
            value={
              <>
                {typeof defaultStopDisplay === "string" ? (
                  defaultStopDisplay
                ) : (
                  <>
                    <div>{defaultStopDisplay?.distance}</div>
                    <div>{defaultStopDisplay?.baseline}</div>
                  </>
                )}
                {!isChannelConfiguration && (
                  <BotOverrideDisplay
                    botData={botData}
                    overrideType={OverrideFieldType.DEFAULT_STOP}
                    isChannelValue={
                      defaultStopDisplay === t(USING_CHANNEL_SETTING)
                    }
                  />
                )}
              </>
            }
            autoHeight={true}
            valueFlex={valueFlex}
          />
        )}
        {!!skipWithoutStopLossDisplay && (
          <ExtraInfoItem
            name={t("labels.skip_signals_without_stop_loss")}
            value={skipWithoutStopLossDisplay}
            valueFlex={valueFlex}
          />
        )}
        {showNumContinuousStops && (
          <ExtraInfoItem
            name={t("labels.number_of_continuous_stops")}
            value={botData.number_of_continuous_stops_till_bot_stop ?? "N/A"}
            autoHeight={true}
            valueFlex={valueFlex}
          />
        )}
        {stopTimeoutData?.shouldDisplay && (
          <ExtraInfoItem
            name={stopTimeoutData.title}
            value={stopTimeoutData.value}
            valueFlex={valueFlex}
          />
        )}
        {showTrailingStop && (
          <ExtraInfoItem
            name={t("labels.trailing_stop")}
            value={
              <TrailingStopDisplay
                botData={botData}
                displayData={trailingStopDisplay}
                isChannelConfiguration={isChannelConfiguration}
              />
            }
            autoHeight={true}
            valueFlex={valueFlex}
          />
        )}
      </>
    );
  };

  const getGeneralSection = () => {
    const entryUuidName = t(
      isTradingViewSignalsBot ? SIGNAL_ENTRY_UUID : ENTRY_UUID
    );
    const takeProfitUuidName = t(
      isTradingViewSignalsBot ? SIGNAL_TAKE_PROFIT_UUID : TAKE_PROFIT_UUID
    );
    const content = (
      <>
        {showName && "name" in botData && (
          <ExtraInfoItem
            name={t("labels.name")}
            value={botData.name}
            valueFlex={valueFlex}
          />
        )}
        {accountData?.shouldDisplay && (
          <ExtraInfoItem
            name={accountData.title}
            value={
              <TextWithIcon
                coinOrExchangeName={accountData.account?.exchange}
                text={accountData.account?.accountName}
                iconType={ICON_TYPES.EXCHANGE}
              />
            }
            valueFlex={valueFlex}
          />
        )}
        {subGroupData?.shouldDisplay && (
          <ExtraInfoItem
            name={subGroupData.title}
            value={
              <TextWithIcon
                iconLink={botData.sub_group_image}
                text={botData.sub_group_name}
                iconStyle={{ borderRadius: 4, marginRight: 8 }}
              />
            }
            valueFlex={valueFlex}
          />
        )}
        {getSymbols()}
        {exchangesData?.shouldDisplay && (
          <ExtraInfoItem
            name={exchangesData.title}
            value={
              <ExchangesIconsGroup
                exchanges={botData.exchanges}
                size={24}
                maxCount={7}
              />
            }
            valueFlex={valueFlex}
          />
        )}
        {showAmount && (
          <ExtraInfoItem
            name={t("labels.amount")}
            autoHeight={true}
            value={
              <>
                {getBotAmountDisplay(botData)}
                {!isChannelConfiguration && (
                  <BotOverrideDisplay
                    botData={botData}
                    overrideType={OverrideFieldType.AMOUNT_PER_TRADE}
                  />
                )}
              </>
            }
            valueFlex={valueFlex}
          />
        )}
        {longShortDisplay && !isHedgingBot && (
          <ExtraInfoItem
            name={t("labels.long_short")}
            value={longShortDisplay}
            valueFlex={valueFlex}
          />
        )}
        {getMarginSummary()}
        {!!backtesting && (
          <ExtraInfoItem
            name={t("labels.initial_funds")}
            titleHelperProps={initialFundsData?.analyticsData}
            value={
              <Flex $justifyContent="flex-start" style={{ columnGap: 16 }}>
                {initialFundsData?.mainCoinsData.map(([coin, funds]) => {
                  return (
                    <TextWithIcon
                      key={coin}
                      iconType={ICON_TYPES.COIN}
                      coinOrExchangeName={coin}
                      text={funds}
                    />
                  );
                })}
              </Flex>
            }
            valueFlex={valueFlex}
          />
        )}
        {isTradingViewBot && botData.entry_uuid && !isSubUser && (
          <ExtraInfoItem
            name={entryUuidName}
            valueFlex={valueFlex}
            autoHeight={true}
            value={
              <TradingViewUUID uuid={botData.entry_uuid} name={entryUuidName} />
            }
          />
        )}
        {isTradingViewBot && botData.take_profit_uuid && !isSubUser && (
          <ExtraInfoItem
            name={takeProfitUuidName}
            valueFlex={valueFlex}
            autoHeight={true}
            value={
              <TradingViewUUID
                uuid={botData.take_profit_uuid}
                name={takeProfitUuidName}
              />
            }
          />
        )}
      </>
    );
    return showGeneralSection
      ? renderSection(t("tabs.general"), content, { marginTop: 0 })
      : content;
  };

  const getAdvancedSection = () => {
    if (!showAdvancedSection) return null;
    return renderSection(
      t("tabs.advanced"),
      <>
        <>
          <>
            {!isChannelConfiguration &&
              getAdvancedKeys(
                botData.bot_type,
                !!user?.hasFeatureFlag(TIME_STOP_FEATURE_FLAG)
              ).map((advancedKey) => {
                let value: string | number | null = botData[advancedKey];
                if (
                  value &&
                  (advancedKey === "auto_cancel_timeout_seconds" ||
                    advancedKey === "time_stop_seconds")
                ) {
                  value = getSecondsTimeoutDisplay(value);
                }
                return (
                  <ExtraInfoItem
                    key={advancedKey}
                    name={t(ADVANCED_KEY_TO_INPUT_DISPLAY_NAME[advancedKey])}
                    value={value ?? t("messages.info.no_limit")}
                    valueFlex={valueFlex}
                  />
                );
              })}
            {cancelOnProfitBeforeEntryDisplayData.shouldDisplay && (
              <ExtraInfoItem
                name={cancelOnProfitBeforeEntryDisplayData.title}
                value={
                  <Flex $justifyContent="flex-start">
                    {cancelOnProfitBeforeEntryDisplayData.value}
                    {!!cancelOnProfitBeforeEntryDisplayData.helperData && (
                      <HelperTooltip
                        {...cancelOnProfitBeforeEntryDisplayData.helperData}
                      />
                    )}
                  </Flex>
                }
                valueFlex={valueFlex}
              />
            )}
            {alternativeUsdPairsData.shouldDisplay && (
              <ExtraInfoItem
                name={alternativeUsdPairsData.title}
                value={alternativeUsdPairsData.value}
                valueFlex={valueFlex}
              />
            )}
            {!!Object.keys(botData.keyword_to_symbol_mapping || {}).length && (
              <ExtraInfoItem
                name={t(KEYWORD_TO_SYMBOL_TITLE)}
                value={
                  <Text>
                    {Object.entries(botData.keyword_to_symbol_mapping || {})
                      .map(
                        ([keyword, symbol]) =>
                          `${capitalizeFirstLetter(
                            keyword.toLowerCase()
                          )}: ${symbol}`
                      )
                      .join(", ")}
                  </Text>
                }
                valueFlex={valueFlex}
              />
            )}
            {!!stopTypeDisplay && (
              <ExtraInfoItem
                name={t("labels.stop_type")}
                value={stopTypeDisplay}
                valueFlex={valueFlex}
              />
            )}
            {!!stopLimitPriceReductionDisplay && (
              <ExtraInfoItem
                name={t("labels.stop_limit_price_reduction")}
                value={stopLimitPriceReductionDisplay}
                valueFlex={valueFlex}
              />
            )}
            {operationHoursData.shouldDisplay && (
              <>
                <ExtraInfoItem
                  name={operationHoursData.title}
                  value={
                    <Flex>
                      <Text>{operationHoursData.value}</Text>
                      {operationHoursData.showButton && (
                        <Link onClick={operationHoursData.onShowMoreClick}>
                          {t("buttons.show_more")}
                        </Link>
                      )}
                    </Flex>
                  }
                  valueFlex={valueFlex}
                />
                <ViewOnlyOperationHours
                  open={operationHoursData.drawerOpen}
                  closeDrawer={operationHoursData.closeDrawer}
                  blacklistedOperationHours={operationHoursData.data}
                />
              </>
            )}
          </>
        </>
      </>
    );
  };

  return (
    <div style={{ paddingBottom: 20, ...style }}>
      {sectionsDisplayData?.isEmptyConfiguration ? (
        <Flex $justifyContent="center" $flexDirection="column">
          <ConfigurationSearch style={{ width: 140 }} />
          <Text
            style={{ marginTop: -10, marginBottom: 12 }}
            $size="L"
            $weight={typography.fontWeights.MEDIUM}
          >
            {t("group_configuration.default_title")}
          </Text>
          <Text type="secondary">
            {t("group_configuration.default_description")}
          </Text>
        </Flex>
      ) : (
        <>
          {getGeneralSection()}
          {getEntriesSection()}
          {!isHedgingBot && (
            <>
              {getTakeProfitsSection()}
              {getStopSection()}
              {getAdvancedSection()}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default BotSummary;

const SectionTitle = styled(Title).attrs({
  level: 3,
})`
  margin-top: 32px;
  font-weight: ${typography.fontWeights.SEMI_BOLD};
  ${(p) =>
    p.theme.isNewLook &&
    `
    margin-top: 28px;
    margin-bottom: 4px;
    color: ${p.theme.colors.titleColor};
    font-size: 15px;
    font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
  `}
`;
const NewLookSection = styled.section`
  margin-top: 16px;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: ${(p) => getPanelShadow(p.theme)};
  &:first-child {
    margin-top: 0;
  }
`;
const NewLookSectionTitle = styled.h3`
  display: flex;
  align-items: center;
  min-height: 44px;
  margin: 0;
  padding: 0 16px;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
`;
// The rows' own hairlines separate them; the last one gives way to the
// panel's edge.
const NewLookSectionBody = styled.div`
  padding: 0 16px;
  > :last-child,
  > :last-child > :last-child {
    border-bottom: none !important;
  }
`;
const StyledLink = styled(Link)<{ $isMobile?: boolean }>`
  padding-left: 8px;
  font-size: ${(p) =>
    p.$isMobile
      ? p.theme.typography.fontSizes.S
      : p.theme.typography.fontSizes.M};
`;
const SectionValue = styled(Text)`
  font-weight: ${typography.fontWeights.MEDIUM};
`;
